import { BASE_URL } from 'config';
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  FormHelperText,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";

const AddBankAccountForm = ({ onClose, onSuccess, initialData }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    account: initialData?.account || "",
    iban: initialData?.iban || "",
    is_default: initialData?.is_default || false,
    id: initialData?.id || null,
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || "",
        account: initialData.account || "",
        iban: initialData.iban || "",
        is_default: initialData.is_default || false,
        id: initialData.id || null,
      });
    }
  }, [initialData]);

  const [errors, setErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (formSubmitted) validateForm();
  }, [formData]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "يجب إدخال اسم البنك";
    if (!formData.account.trim()) {
      newErrors.account = "يجب إدخال رقم الحساب";
    } else if (!/^[0-9]+$/.test(formData.account)) {
      newErrors.account = "رقم الحساب يجب أن يحتوي على أرقام فقط";
    } else if (formData.account.length > 21) {
      newErrors.account = "رقم الحساب يجب ألا يتجاوز 21 رقمًا";
    }
    if (!formData.iban.trim()) newErrors.iban = "يجب إدخال رقم الآيبان";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "account") {
      // اسمح فقط بالأرقام، ولا تتجاوز 21 رقمًا
      const numericValue = value.replace(/[^0-9]/g, "").slice(0, 21);
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    if (!validateForm()) return;
    try {
      Swal.fire({
        title: formData.id ? "جاري التعديل..." : "جاري الإضافة...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const container = document.querySelector(".swal2-container");
          if (container) container.style.zIndex = "99999";
        },
      });
      const token = localStorage.getItem("user-token");
      const endpoint = formData.id
        ? `${BASE_URL}/manager/bank-accounts/update/${formData.id}`
        : BASE_URL + "/manager/bank-accounts/store";
      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      Swal.close();
      if (response.data.status) {
        await Swal.fire({
          icon: "success",
          title: "تم الحفظ!",
          text: "تم حفظ الحساب البنكي بنجاح",
          confirmButtonText: "موافق",
          didOpen: () => {
            const container = document.querySelector(".swal2-container");
            if (container) container.style.zIndex = "99999";
          },
        });
        onSuccess && onSuccess();
        onClose && onClose();
      } else {
        throw new Error(response.data.message || "حدث خطأ غير متوقع");
      }
    } catch (error) {
      Swal.close();
      const errorMessage = error.response?.data?.message || error.message || "حدث خطأ أثناء الحفظ";
      Swal.fire({
        icon: "error",
        title: "فشل الحفظ!",
        text: errorMessage,
        confirmButtonText: "موافق",
        didOpen: () => {
          const container = document.querySelector(".swal2-container");
          if (container) container.style.zIndex = "99999";
        },
      });
    } finally {
      setFormSubmitted(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: "",
      account: "",
      iban: "",
      is_default: false,
      id: null,
    });
    setErrors({});
    onClose && onClose();
  };

  return (
    <Box
      sx={{
        p: 3,
        width: "100%",
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          {formData.id ? "تعديل حساب بنكي" : "إضافة حساب بنكي جديد"}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.name} sx={{ mb: 2 }}>
              <TextField
                label="اسم البنك *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.account} sx={{ mb: 2 }}>
              <TextField
                label="رقم الحساب *"
                name="account"
                value={formData.account}
                onChange={handleChange}
                error={!!errors.account}
                helperText={errors.account}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.iban} sx={{ mb: 2 }}>
              <TextField
                label="رقم الآيبان *"
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                error={!!errors.iban}
                helperText={errors.iban}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_default}
                  onChange={handleChange}
                  name="is_default"
                  color="primary"
                />
              }
              label="اجعل هذا الحساب افتراضي"
            />
          </Grid>
        </Grid>
        <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="textSecondary">
            * الحقول المطلوبة
          </Typography>
          <Box display="flex" gap={2}>
            <Button variant="contained" color="error" onClick={handleCancel} sx={{ px: 4 }}>
              إلغاء
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ px: 4, color: "white !important" }}
            >
              حفظ الحساب
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default AddBankAccountForm;
